import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Typography,
} from "@mui/material";
import { ArrowBack, Restore, History } from "@mui/icons-material";
import { useState } from "react";
import apiClient from "@/api/client";
import { useAgent } from "@/hooks/useAgents";

interface ConfigHistory {
  id: string;
  agent_id: string;
  config_snapshot: Record<string, unknown>;
  changed_by: string;
  change_reason: string | null;
  created_at: string;
  is_rollback_target: boolean;
}

export default function AgentHistory() {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: agent } = useAgent(agentId);

  const [rollbackTarget, setRollbackTarget] = useState<ConfigHistory | null>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });

  const { data, isLoading, error } = useQuery<ConfigHistory[]>({
    queryKey: ["agent-history", agentId],
    queryFn: async () => {
      const res = await apiClient.get<{ history: ConfigHistory[] }>(`/agents/${agentId}/history`);
      return res.data.history;
    },
    enabled: !!agentId,
  });

  const rollbackMutation = useMutation({
    mutationFn: async (historyId: string) => {
      await apiClient.post(`/agents/${agentId}/rollback/${historyId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agents"] });
      queryClient.invalidateQueries({ queryKey: ["agent-history", agentId] });
      setRollbackTarget(null);
      setSnackbar({ open: true, message: "Agent rolled back successfully", severity: "success" });
    },
    onError: () => {
      setSnackbar({ open: true, message: "Rollback failed", severity: "error" });
    },
  });

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(`/agents/${agentId}`)}>
          Back
        </Button>
        <History color="action" />
        <Typography variant="h4">
          Config History: {agent?.code ?? ""} {agent?.name ?? ""}
        </Typography>
      </Box>

      {error && <Alert severity="error">Failed to load history.</Alert>}

      {data && data.length === 0 && (
        <Alert severity="info">No config changes recorded yet for this agent.</Alert>
      )}

      {data?.map((entry, i) => (
        <Card key={entry.id} sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                Version {data.length - i}
              </Typography>
              {entry.is_rollback_target && (
                <Chip label="Latest snapshot" size="small" color="primary" variant="outlined" />
              )}
              <Box sx={{ ml: "auto", display: "flex", gap: 1, alignItems: "center" }}>
                <Typography variant="caption" color="text.secondary">
                  {new Date(entry.created_at).toLocaleString()}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  startIcon={<Restore />}
                  onClick={() => setRollbackTarget(entry)}
                >
                  Rollback
                </Button>
              </Box>
            </Box>

            {entry.change_reason && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Reason: {entry.change_reason}
              </Typography>
            )}

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 1,
                mt: 1,
              }}
            >
              {Object.entries(entry.config_snapshot).map(([key, val]) => {
                if (val == null) return null;
                const display =
                  typeof val === "object" ? JSON.stringify(val, null, 0) : String(val);
                if (display.length > 200) {
                  return (
                    <Box key={key}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        {key}
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                        {display.slice(0, 200)}...
                      </Typography>
                    </Box>
                  );
                }
                return (
                  <Box key={key}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      {key}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                      {display}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </CardContent>
        </Card>
      ))}

      {/* Rollback confirmation dialog */}
      <Dialog open={!!rollbackTarget} onClose={() => setRollbackTarget(null)}>
        <DialogTitle>Confirm Rollback</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will revert the agent to the configuration from{" "}
            <strong>
              {rollbackTarget && new Date(rollbackTarget.created_at).toLocaleString()}
            </strong>
            . The current config will be saved to history before the rollback.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRollbackTarget(null)}>Cancel</Button>
          <Button
            color="warning"
            variant="contained"
            onClick={() => rollbackTarget && rollbackMutation.mutate(rollbackTarget.id)}
            disabled={rollbackMutation.isPending}
          >
            {rollbackMutation.isPending ? "Rolling back..." : "Rollback"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
